import classNames from 'classnames'
import { ChevronDown, ChevronRight } from 'lucide-react'

import {
  advanceRange,
  autoGroupStructure,
  bracketSizeFor,
  describeGroupSizes,
  evenSplits,
  groupCountRange,
  isPowerOfTwo,
  manualGroupStructure,
  roundRobinLoad,
  splitTeams,
} from '../groupStructure'
import type { GroupPlayUiConfig } from '../payload'
import type { TeamFormation } from '../types'
import { ChipGroup, FieldError, Help, NumberStepper, SwitchRow } from '../ui'

/**
 * Pick an advancing count that fills a power-of-2 bracket (no byes), closest
 * to half of each group; falls back to half the group when nothing fits.
 */
export const suggestAdvance = (groups: number, groupSize: number, max: number) => {
  const target = groupSize / 2
  let best: number | null = null
  for (let a = 1; a <= max; a++) {
    if (!isPowerOfTwo(groups * a) || groups * a < 2) continue
    if (best === null || Math.abs(a - target) <= Math.abs(best - target)) best = a
  }
  return best ?? Math.max(1, Math.min(max, Math.floor(target)))
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n))

const label = 'text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary'

export const GroupStageControls = ({
  config,
  setConfig,
  teamFormation,
  numberOfTeams,
  playFormat,
}: {
  config: GroupPlayUiConfig
  setConfig: (next: GroupPlayUiConfig) => void
  teamFormation: TeamFormation
  numberOfTeams?: string
  playFormat?: string
}) => {
  const solo = teamFormation !== 'teams'
  const teamCount = solo ? null : parseInt(numberOfTeams || '') || null
  const doubleElim = playFormat === 'double_elimination'
  const manual = config.structureMode === 'manual'

  if (!solo && (!teamCount || teamCount < 4)) {
    return <Help>Pick how many teams can join (4 or more) to set up the groups.</Help>
  }

  const auto = teamCount ? autoGroupStructure(teamCount, config.playInEnabled) : null
  const groupRange = groupCountRange(teamCount)

  // What the controls show: the organizer's numbers, or the automatic ones.
  const shownGroups = manual && config.numberOfGroups ? config.numberOfGroups : auto?.numberOfGroups ?? 2
  const advRangeFor = (g: number) => advanceRange(teamCount, g)
  const shownAdvance =
    manual && config.advancePerGroup
      ? config.advancePerGroup
      : auto?.advancePerGroup ?? clamp(2, advRangeFor(shownGroups).min, advRangeFor(shownGroups).max)

  const setManual = (groups: number, advance?: number) => {
    const g = clamp(groups, groupRange.min, groupRange.max)
    const range = advRangeFor(g)
    const a = advance ?? (config.advancePerGroup ?? shownAdvance)
    setConfig({ ...config, structureMode: 'manual', numberOfGroups: g, advancePerGroup: clamp(a, range.min, range.max) })
  }

  const pickSplit = (g: number) => {
    const size = teamCount ? Math.floor(teamCount / g) : 4
    setManual(g, suggestAdvance(g, size, advRangeFor(g).max))
  }

  const check = manual
    ? manualGroupStructure({ teamCount, numberOfGroups: shownGroups, advancePerGroup: shownAdvance, doubleElim })
    : null

  /* ---------------------------------------------------------------------- */
  /* Solo signup: team count unknown until signups close                    */
  /* ---------------------------------------------------------------------- */
  if (solo) {
    const total = shownGroups * shownAdvance
    const bracket = bracketSizeFor(total)
    return (
      <div className="grid gap-4" data-field="group_structure">
        <div>
          <ChipGroup
            label="Groups"
            options={[
              { value: 'auto', label: 'Automatic' },
              { value: 'manual', label: 'Set number of groups' },
            ]}
            value={config.structureMode}
            onChange={(v) => (v === 'manual' ? setManual(config.numberOfGroups ?? 2, config.advancePerGroup ?? 2) : setConfig({ ...config, structureMode: 'auto' }))}
          />
          <Help>
            {manual
              ? 'Teams are formed after signups close, so group sizes depend on how many players join. Teams are spread evenly across your groups.'
              : 'Groups are worked out when signups close, from the teams that get formed.'}
          </Help>
        </div>
        {manual && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Stepper id="ct-groups-count" title="Number of groups" value={shownGroups} min={1} max={16} onChange={(n) => setManual(n)} suffix={shownGroups === 1 ? 'group' : 'groups'} />
              <Stepper
                id="ct-groups-advance"
                title="Advancing per group"
                value={shownAdvance}
                min={1}
                max={advRangeFor(shownGroups).max}
                onChange={(n) => setManual(shownGroups, n)}
                suffix="per group"
              />
            </div>
            <StatRow
              stats={[
                [String(shownGroups), shownGroups === 1 ? 'group' : 'groups'],
                [String(shownAdvance), 'advance each'],
                [String(bracket), 'bracket spots'],
              ]}
            />
            <StructureDiagram groupSizes={null} numberOfGroups={shownGroups} advancePerGroup={shownAdvance} bracketSize={bracket} byes={bracket - total} playInSpots={0} />
            {check?.error ? (
              <FieldError message={check.error} />
            ) : (
              <Help>
                Top {shownAdvance} of each group reach a bracket of {bracket}
                {bracket > total ? `; the top ${bracket - total} seeds get a first-round bye` : ''}. You’ll need at least{' '}
                {shownGroups * (shownAdvance + 1)} teams when the tournament starts.
              </Help>
            )}
          </>
        )}
        <GrandFinal config={config} setConfig={setConfig} />
      </div>
    )
  }

  /* ---------------------------------------------------------------------- */
  /* Teams register: plan around the capacity                               */
  /* ---------------------------------------------------------------------- */
  const n = teamCount as number
  const sizes = manual ? splitTeams(n, shownGroups) : auto?.groupSizes ?? splitTeams(n, shownGroups)
  const total = shownGroups * shownAdvance
  const bracket = manual ? bracketSizeFor(total) : auto?.bracketSize ?? bracketSizeFor(total)
  const playInSpots = manual ? 0 : auto?.playInSpots ?? 0
  const byes = Math.max(0, bracket - total - playInSpots)
  const load = roundRobinLoad(sizes)
  const uneven = Math.max(...sizes) !== Math.min(...sizes)
  const splits = evenSplits(n)
  const advRange = advRangeFor(shownGroups)

  return (
    <div className="grid gap-4" data-field="group_structure">
      <div>
        <ChipGroup
          label="Group structure"
          options={[
            { value: 'auto', label: 'Automatic' },
            { value: 'manual', label: 'Custom' },
          ]}
          value={config.structureMode}
          onChange={(v) => (v === 'manual' ? setManual(shownGroups, shownAdvance) : setConfig({ ...config, structureMode: 'auto' }))}
        />
        <Help>
          {manual
            ? `If fewer than ${n} teams sign up, your ${shownGroups === 1 ? 'group is' : `${shownGroups} groups are`} kept and teams are spread evenly.`
            : 'We size the groups from the teams that actually sign up. Showing the plan for a full field.'}
        </Help>
      </div>

      {splits.length > 0 && (
        <ChipGroup
          label="Quick pick"
          options={splits.map((s) => ({ value: String(s.groups), label: `${s.groups} × ${s.size}` }))}
          value={manual ? String(shownGroups) : ''}
          onChange={(v) => pickSplit(parseInt(v))}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Stepper
          id="ct-groups-count"
          title="Number of groups"
          value={shownGroups}
          min={groupRange.min}
          max={groupRange.max}
          onChange={(g) => setManual(g)}
          suffix={
            shownGroups === 1
              ? `group of ${sizes[0]}`
              : uneven
                ? `groups of ${Math.max(...sizes)} and ${Math.min(...sizes)}`
                : `groups of ${sizes[0]}`
          }
        />
        <Stepper
          id="ct-groups-advance"
          title="Advancing per group"
          value={shownAdvance}
          min={advRange.min}
          max={advRange.max}
          onChange={(a) => setManual(shownGroups, a)}
          suffix="per group"
        />
      </div>

      <StatRow
        stats={[
          [String(shownGroups), shownGroups === 1 ? 'group' : 'groups'],
          [uneven ? `${Math.max(...sizes)}/${Math.min(...sizes)}` : String(sizes[0]), 'teams per group'],
          [String(shownAdvance), 'advance each'],
          [String(bracket), 'bracket spots'],
        ]}
      />

      <StructureDiagram groupSizes={sizes} numberOfGroups={shownGroups} advancePerGroup={shownAdvance} bracketSize={bracket} byes={byes} playInSpots={playInSpots} />

      {check?.error ? (
        <FieldError message={check.error} />
      ) : (
        <Help>
          {describeGroupSizes(sizes)}: {load.perTeam} {load.perTeam === 1 ? 'match' : 'matches'} per team, {load.total} group matches in total (best of 1).{' '}
          {uneven ? 'Teams in the bigger groups play one extra match. ' : ''}
          {total + playInSpots === bracket
            ? `Top ${shownAdvance}${playInSpots ? ` plus ${playInSpots} play-in winners` : ''} fill a bracket of ${bracket}.`
            : `${total + playInSpots} teams reach a bracket of ${bracket}, so the top ${byes} seeds get a first-round bye.`}
        </Help>
      )}

      {!manual && (
        <SwitchRow
          id="ct-play-in"
          checked={config.playInEnabled}
          onChange={(v) => setConfig({ ...config, playInEnabled: v })}
          label="Play-in round"
          description="Teams on the bubble play a best-of-3 for the last bracket spots when groups don’t divide evenly."
        />
      )}

      <GrandFinal config={config} setConfig={setConfig} />
    </div>
  )
}

const Stepper = ({
  id,
  title,
  value,
  min,
  max,
  onChange,
  suffix,
}: {
  id: string
  title: string
  value: number
  min: number
  max: number
  onChange: (n: number) => void
  suffix?: string
}) => (
  <div>
    <label htmlFor={id} className={classNames('mb-1.5 block', label)}>
      {title}
    </label>
    <NumberStepper id={id} label={title} value={value} min={min} max={Math.max(min, max)} onChange={onChange} suffix={suffix} />
  </div>
)

const StatRow = ({ stats }: { stats: [string, string][] }) => (
  <div className={classNames('grid gap-2', stats.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3')}>
    {stats.map(([value, caption]) => (
      <div key={caption} className="rounded-lg bg-light-bg-secondary dark:bg-dark-bg px-3 py-2 text-center">
        <div className="font-display text-xl font-bold tabular-nums text-light-typo-primary dark:text-dark-typo-primary">{value}</div>
        <div className="text-[11px] text-light-typo-gray dark:text-dark-typo-gray">{caption}</div>
      </div>
    ))}
  </div>
)

const GrandFinal = ({ config, setConfig }: { config: GroupPlayUiConfig; setConfig: (next: GroupPlayUiConfig) => void }) => (
  <div>
    <ChipGroup
      label="Grand final"
      options={[
        { value: 'bo3', label: 'Best of 3' },
        { value: 'bo5', label: 'Best of 5' },
      ]}
      value={config.grandFinalsFormat}
      onChange={(v) => setConfig({ ...config, grandFinalsFormat: v })}
    />
    <Help>
      Groups are best of 1{config.structureMode === 'auto' && config.playInEnabled ? ', play-ins best of 3' : ''}, bracket matches best of 3.
    </Help>
  </div>
)

const MAX_GROUP_COLUMNS = 8
const MAX_BRACKET_SLOTS = 16

/** Compact picture: group columns (advancing slots lit) → bracket slots. */
export const StructureDiagram = ({
  groupSizes,
  numberOfGroups,
  advancePerGroup,
  bracketSize,
  byes,
  playInSpots,
}: {
  /** null for solo signup (sizes unknown). */
  groupSizes: number[] | null
  numberOfGroups: number
  advancePerGroup: number
  bracketSize: number
  byes: number
  playInSpots: number
}) => {
  const shown = Math.min(numberOfGroups, MAX_GROUP_COLUMNS)
  const hidden = numberOfGroups - shown
  const advancing = Math.min(bracketSize, numberOfGroups * advancePerGroup)
  const slots = Math.min(bracketSize, MAX_BRACKET_SLOTS)
  const scale = bracketSize / slots
  const filled = Math.round(advancing / scale)
  const playIn = Math.round(playInSpots / scale)

  return (
    <figure className="flex flex-col items-stretch gap-3 rounded-lg border border-light-border-primary/70 dark:border-dark-border-primary/70 p-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2" aria-hidden>
        {Array.from({ length: shown }, (_, g) => {
          const size = groupSizes ? groupSizes[g] : null
          const cells = size ?? advancePerGroup + 1
          const cols = cells > 10 ? 'grid-cols-2' : 'grid-cols-1'
          return (
            <div key={g} className="flex w-9 flex-col items-center gap-1">
              <div className={classNames('grid w-full gap-[3px]', cols)}>
                {Array.from({ length: cells }, (_, i) => (
                  <span
                    key={i}
                    className={classNames(
                      'h-1.5 rounded-full',
                      i < advancePerGroup
                        ? 'bg-green'
                        : size === null
                          ? 'border border-dashed border-light-border-primary dark:border-dark-border-primary'
                          : 'bg-light-border-primary dark:bg-dark-border-primary',
                    )}
                  />
                ))}
              </div>
              <span className="font-display text-xs font-bold text-light-typo-gray dark:text-dark-typo-gray">{String.fromCharCode(65 + g)}</span>
            </div>
          )
        })}
        {hidden > 0 && (
          <span className="mb-4 rounded bg-light-bg-secondary dark:bg-dark-bg px-1.5 py-0.5 text-[11px] font-semibold text-light-typo-gray dark:text-dark-typo-gray">
            +{hidden} more
          </span>
        )}
      </div>

      <span className="flex justify-center text-light-typo-gray dark:text-dark-typo-gray" aria-hidden>
        <ChevronRight size={18} className="hidden sm:block" />
        <ChevronDown size={18} className="sm:hidden" />
      </span>

      <div className="flex flex-col items-center gap-1 sm:w-24" aria-hidden>
        <div className="grid w-full max-w-[9rem] grid-cols-2 gap-[3px]">
          {Array.from({ length: slots }, (_, i) => (
            <span
              key={i}
              className={classNames(
                'h-1.5 rounded-full',
                i < filled
                  ? 'bg-btn-fancy-primary-end'
                  : i < filled + playIn
                    ? 'bg-yellow/80'
                    : 'border border-dashed border-light-border-primary dark:border-dark-border-primary',
              )}
            />
          ))}
        </div>
        <span className="font-display text-xs font-bold text-light-typo-gray dark:text-dark-typo-gray">
          Bracket of {bracketSize}
          {byes > 0 ? ` · ${byes} bye${byes === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      <figcaption className="sr-only">
        {groupSizes ? describeGroupSizes(groupSizes) : `${numberOfGroups} groups`}, top {advancePerGroup} advance, bracket of {bracketSize}
        {byes > 0 ? ` with ${byes} first-round byes` : ''}
        {playInSpots > 0 ? `, ${playInSpots} spots decided in the play-in` : ''}.
      </figcaption>
    </figure>
  )
}
